import type { z } from "zod";

/**
 * Комбинированный подход к валидации:
 * 1. Сначала пытается строгая валидация
 * 2. При ошибке - мягкая валидация (partial + passthrough)
 * 3. В крайнем случае - возвращает исходные данные
 * Логирует ошибки на всех этапах, но не падает приложение
 */
export const safeParseWithFallback = <T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  context: string,
): z.infer<T> => {
  // Шаг 1: Строгая валидация
  const strictResult = schema.safeParse(data);

  if (strictResult.success) {
    return strictResult.data;
  }

  // Логируем ошибки строгой валидации
  const logLevel = typeof window === "undefined" ? "error" : "warn";

  // console[logLevel](`[Zod Validation] ${context} - Strict validation failed:`, {
  //   issues: strictResult.error.issues,
  //   formatted: strictResult.error.format(),
  // });

  // Шаг 2: Мягкая валидация (partial + passthrough)
  try {
    // Пытаемся создать мягкую версию схемы
    let softSchema: z.ZodTypeAny = schema;

    // Проверяем тип схемы через _def
    const schemaDef = (schema as any)._def;
    const typeName = schemaDef?.typeName;

    // Если это объект - делаем его partial и passthrough
    if (typeName === "ZodObject") {
      softSchema = (schema as unknown as z.ZodObject<any>).partial();
    } else if (typeName === "ZodEffects") {
      // Для ZodEffects пытаемся получить внутреннюю схему
      const innerSchema = schemaDef.schema;
      if (innerSchema && (innerSchema as any)._def?.typeName === "ZodObject") {
        softSchema = (innerSchema as unknown as z.ZodObject<any>).partial();
      }
    }

    const softResult = softSchema.safeParse(data);

    if (softResult.success) {
      // console[logLevel === "error" ? "warn" : "log"](
      //   `[Zod Validation] ${context} - Using partially validated data`,
      // );
      return softResult.data as z.infer<T>;
    }
  } catch (e) {
    // Если не удалось создать мягкую схему - пропускаем этот шаг
    // console[logLevel === "error" ? "warn" : "log"](
    //   `[Zod Validation] ${context} - Could not create soft schema, using raw data`,
    // );
  }

  // Шаг 3: Возвращаем исходные данные
  // console[logLevel](
  //   `[Zod Validation] ${context} - Returning raw data (validation failed)`,
  // );

  return data as z.infer<T>;
};
